import useScopedCommerces from '@/pages/BusinessCommerces/hooks/useScopedCommerces';
import { useRouteVisitsExport } from '@/pages/Business/pages/Monitoring/hooks';
import { AutocompleteInput, DateInput, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { MS_PER_DAY, VISITS_REPORT_MAX_RANGE_DAYS } from '../constants';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { endOfLocalDay, startOfLocalDay } from '@/utils/dates';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

interface VisitsReportDialogProps {
  open: boolean;
  onClose: () => void;
}

const VisitsReportDialog = ({ open, onClose }: VisitsReportDialogProps) => {
  const { commerces, isMulti } = useScopedCommerces();
  const { handleExport, isExporting } = useRouteVisitsExport();

  const [commerce, setCommerce] = useState<Option | null>(null);
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  useEffect(() => {
    if (isMulti || commerces.length === 0) return;
    setCommerce((current) => current ?? commerces[0]);
  }, [isMulti, commerces]);

  const range = useMemo(() => {
    const scheduledFrom = startOfLocalDay(from);
    const scheduledTo = endOfLocalDay(to);
    if (!scheduledFrom || !scheduledTo) return null;
    return { scheduledFrom, scheduledTo };
  }, [from, to]);

  const validationError = useMemo(() => {
    if (isMulti && !commerce) return 'Elegí el comercio del reporte.';
    if (!range) return 'Indicá el rango de fechas.';
    if (range.scheduledFrom.getTime() > range.scheduledTo.getTime()) {
      return 'La fecha desde tiene que ser anterior a la fecha hasta.';
    }
    const spanMs =
      range.scheduledTo.getTime() - range.scheduledFrom.getTime();
    if (spanMs > VISITS_REPORT_MAX_RANGE_DAYS * MS_PER_DAY) {
      return `El rango no puede superar ${VISITS_REPORT_MAX_RANGE_DAYS} días.`;
    }
    return null;
  }, [isMulti, commerce, range]);

  const handleClose = () => {
    if (isExporting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (validationError || !range) return;

    await handleExport({
      commerceId: (commerce?.data as string) ?? undefined,
      scheduledFrom: range.scheduledFrom.toISOString(),
      scheduledTo: range.scheduledTo.toISOString(),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>Reporte de visitas</DialogTitle>
      <DialogContent>
        <div className='flex flex-col gap-4 pt-2'>
          <p className='text-sm text-gray-600'>
            Se exporta un Excel con las visitas de tus pedidos en el rango de
            fechas programadas de ruta. Cuando esté listo se descarga solo, y
            queda disponible en Mis archivos.
          </p>

          {isMulti && (
            <AutocompleteInput
              name='report-commerce'
              placeholder='Comercio'
              value={commerce}
              onChange={setCommerce}
              onInputChange={() => {}}
              options={commerces}
              required
              forcePopupIcon
            />
          )}

          <DateInput
            name='report-scheduled-from'
            placeholder='Fecha de ruta desde'
            value={from}
            onChange={setFrom}
            required
          />
          <DateInput
            name='report-scheduled-to'
            placeholder='Fecha de ruta hasta'
            value={to}
            onChange={setTo}
            required
          />

          <p className='text-xs text-gray-500'>
            El rango no puede superar {VISITS_REPORT_MAX_RANGE_DAYS} días.
          </p>

          {validationError && from && to && (
            <p className='text-sm text-red-600'>{validationError}</p>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={handleClose}
          disabled={isExporting}
        >
          Cancelar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSubmit}
          disabled={Boolean(validationError) || isExporting}
        >
          {isExporting ? 'Generando...' : 'Generar reporte'}
        </GlobalButton>
      </DialogActions>
    </Dialog>
  );
};

export default VisitsReportDialog;
